import { Injectable } from '@angular/core';

export interface TableColumnConfig {
  order: string[];
  hidden: string[];
}

const STORAGE_KEY = 'TableColumnConfig';

@Injectable({
  providedIn: 'root',
})
export class TableColumnConfigService {
  getConfig(tableKey: string): TableColumnConfig | null {
    if (!tableKey) return null;

    const allConfigs = this.readAll();
    const scopedKey = this.buildScopedKey(tableKey);
    let config = allConfigs[scopedKey];

    if (!config) {
      // Backward compatibility: read legacy unscoped key and migrate to scoped key.
      const legacyKey = tableKey.trim().toLowerCase();
      const legacy = allConfigs[legacyKey] ?? allConfigs[tableKey];
      if (!legacy) return null;

      config = {
        order: this.normalizeIds(legacy.order),
        hidden: this.normalizeIds(legacy.hidden),
      };

      allConfigs[scopedKey] = config;
      delete allConfigs[legacyKey];
      delete allConfigs[tableKey];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allConfigs));
    }

    return {
      order: this.normalizeIds(config.order),
      hidden: this.normalizeIds(config.hidden),
    };
  }

  saveConfig(tableKey: string, config: TableColumnConfig): void {
    if (!tableKey) return;

    const allConfigs = this.readAll();
    allConfigs[this.buildScopedKey(tableKey)] = {
      order: this.normalizeIds(config.order),
      hidden: this.normalizeIds(config.hidden),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(allConfigs));
  }

  applyConfig<T extends { id: string }>(
    tableKey: string,
    columns: readonly T[],
    fallbackHidden: readonly string[] = []
  ): TableColumnConfig & { columns: T[] } {
    const normalizedFallbackHidden = this.normalizeIds(fallbackHidden as string[]);
    const saved = this.getConfig(tableKey);

    const currentColumns = [...columns];
    const currentIds = currentColumns.map(column => this.normalizeId(column.id));

    const order = this.normalizeOrder(saved?.order ?? currentIds, currentIds);
    const hidden = this.normalizeHidden(saved?.hidden, normalizedFallbackHidden, currentIds);

    const sortedColumns = this.sortColumnsByOrder(currentColumns, order);

    return {
      order,
      hidden,
      columns: sortedColumns,
    };
  }

  private sortColumnsByOrder<T extends { id: string }>(columns: readonly T[], order: readonly string[]): T[] {
    const indexMap = new Map(order.map((id, index) => [id, index]));

    return [...columns].sort((a, b) => {
      const aIndex = indexMap.get(this.normalizeId(a.id)) ?? Number.MAX_SAFE_INTEGER;
      const bIndex = indexMap.get(this.normalizeId(b.id)) ?? Number.MAX_SAFE_INTEGER;
      return aIndex - bIndex;
    });
  }

  private normalizeHidden(
    hiddenFromConfig: readonly string[] | undefined,
    fallbackHidden: readonly string[],
    availableIds: readonly string[]
  ): string[] {
    const available = new Set(availableIds);
    const source = hiddenFromConfig == null ? fallbackHidden : hiddenFromConfig;
    return this.normalizeIds(source).filter(id => available.has(id));
  }

  private normalizeOrder(orderFromConfig: readonly string[], availableIds: readonly string[]): string[] {
    const available = new Set(availableIds);
    const validConfigured = this.normalizeIds(orderFromConfig).filter(id => available.has(id));
    const missing = availableIds.filter(id => !validConfigured.includes(id));
    return [...validConfigured, ...missing];
  }

  private normalizeIds(ids: readonly string[] | undefined): string[] {
    if (!ids?.length) return [];

    const unique = new Set<string>();
    ids.forEach(id => unique.add(this.normalizeId(id)));
    return [...unique];
  }

  private normalizeId(value: string): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private buildScopedKey(tableKey: string): string {
    const db = (localStorage.getItem('DB') ?? localStorage.getItem('FormatCurDB') ?? 'default').trim().toLowerCase();
    const user = (
      sessionStorage.getItem('User') ??
      localStorage.getItem('loginEmail') ??
      localStorage.getItem('username') ??
      'anonymous'
    )
      .trim()
      .toLowerCase();

    return [db, user, tableKey.trim().toLowerCase()].join('::');
  }

  private readAll(): Record<string, TableColumnConfig> {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return Object.create(null) as Record<string, TableColumnConfig>;

    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed ? parsed : (Object.create(null) as Record<string, TableColumnConfig>);
    } catch {
      return Object.create(null) as Record<string, TableColumnConfig>;
    }
  }
}
